import { useTranslation } from "react-i18next"
import { FilterContainer } from "../components/FilterContainer"
import { SearchBar } from "../components/navbar/SearchBar"
import { ListingListView } from "../components/listing/ListingListView";
import { ListingListViewCarousel } from "../components/listing/ListingListViewCarousel";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HandymanIcon from '@mui/icons-material/Handyman';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import LanguageSelector from "../components/main_page/LanguageSelector"
import { MainMenuButton } from "../components/navbar/MainMenuButton"
import { useAppSelector } from "../store/hooks"
import { PrimaryButton } from "../components/buttons/PrimaryButton";
import { useLocation, useNavigate } from "react-router-dom";
import logo from '../assets/images/Rentic_logo.svg';
import filterIcon from '../assets/images/filter_icon.svg';
import closeIcon from '../assets/images/close.svg';
import { NavLink } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { DisplayType } from "../store/features/displaySlice";
import { Categories } from "../components/main_page/Categories";
import { Helmet } from "react-helmet-async";
import bgElement1 from '../assets/images/backgrounds/rentic_bg_element1.svg';
import bgElement2 from '../assets/images/backgrounds/rentic_bg_element2.svg';
import bgElement3 from '../assets/images/backgrounds/rentic_bg_element3.svg';
import { getBackgroundPublic } from "../api/requests/background";
import { BackgroundPublicModel } from "../api/requests/interfaces/Background";

export interface MainPageMobileProps {
    showSearchBar: boolean;
}

export const MainPageMobile: React.FC<MainPageMobileProps> = (props) => {
    const { t } = useTranslation("main");
    const user = useAppSelector(state => state.auth.user);
    const navigate = useNavigate();

    const location = useLocation();

    const searchBarRef = useRef<HTMLDivElement | null>(null);
    const fixedFilterContainerRef = useRef<HTMLDivElement | null>(null);
    const isCollidingRef = useRef(false); // Ref to store the latest collision state
    const [searchBarColliding, setSearchBarColliding] = useState(false);
    const [filterOpened, setFilterOpened] = useState(false);
    const [maxFiltersHeight, setMaxFiltersHeight] = useState(0);
    const [windowScrollY, setWindowScrollY] = useState(0);
    const [backgroundConfig, setBackgroundConfig] = useState<BackgroundPublicModel | null>(null);

    const displayType = useAppSelector((state) => state.display.type);



    const handleScroll = (e: any) => {
        setWindowScrollY(window.scrollY);
        const element = searchBarRef.current;


        if (!element) return;

        // Get the bounding rectangle of the element
        const rect = element.getBoundingClientRect();

        if (rect.top <= 64) {
            if (!isCollidingRef.current) {
                isCollidingRef.current = true; // Update the ref
                setSearchBarColliding(true); // Trigger re-render
                console.log("true");
            }
        } else {
            if (isCollidingRef.current) {
                isCollidingRef.current = false; // Update the ref
                setSearchBarColliding(false); // Trigger re-render
                console.log("false");
            }
        }
    };

    useEffect(() => {
        // Fetch background configuration
        const fetchBackground = async () => {
            try {
                const bg = await getBackgroundPublic();
                setBackgroundConfig(bg);
            } catch (error) {
                console.error("Failed to fetch background:", error);
            }
        };
        fetchBackground();

        handleScroll(undefined);

        if (window.innerWidth > 1024) {
            setSearchBarColliding(false);
            return;
        };

        // Add scroll event listener
        window.addEventListener('scroll', (e) => handleScroll(e));

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('scroll', (e) => handleScroll(e));
        };


    }, [location.pathname]);

    useEffect(() => {
        if (fixedFilterContainerRef.current && !filterOpened) {
            console.log("SEARCH BOTTOM ", window.innerHeight - fixedFilterContainerRef.current.getBoundingClientRect().bottom)
            setMaxFiltersHeight(window.innerHeight - fixedFilterContainerRef.current.getBoundingClientRect().bottom)
            fixedFilterContainerRef.current.style.maxHeight = (window.innerHeight - fixedFilterContainerRef.current.getBoundingClientRect().bottom).toString() + "px"
        }
    }, [fixedFilterContainerRef, windowScrollY])

    return (
        <div className="flex flex-col">

            <Helmet>
                <title>Rentic - Rendi Ükskõik Mida</title>
                <meta name="description" content="Otsi või pane asi renti kiirelt ja lihtsalt. Renticus on lai valik erinevaid asju." />
                <meta name="keywords" content="pane asi renti, asjade rent, autorent, laenuta auto, rendifirma, Rentic, rent anything, rendi asju, easy rentals, online rental service, item rental" />
                <meta property="og:title" content="Rentic - Rendi Ükskõik Mida" />
                <meta property="og:description" content="Otsi või pane enda ese renti kiirelt ja lihtsalt. Renticus on lai valik erinevaid asju." />
                <meta property="og:image" content="/logo512.png" />
                <meta property="og:url" content="https://rentic.ee" />
            </Helmet>

            <div className={`${props.showSearchBar ? 'h-[55vh] pt-[100px] px-4 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100' : 'h-[60px]'} relative flex flex-col justify-start lg:pt-0 gap-5 lg:justify-center lg:items-center items-start mb-12`}>
                {/* Background image (if configured) */}
                {props.showSearchBar && backgroundConfig?.imageUrl && (
                    <div 
                        className={`absolute inset-0 bg-cover bg-center bg-no-repeat ${backgroundConfig.imageOpacity}`}
                        style={{ backgroundImage: `url(${backgroundConfig.imageUrl})` }}
                    />
                )}
                
                {/* Background decorative elements */}
                {props.showSearchBar && backgroundConfig?.showDecorativeElements && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <img src={bgElement1} alt="" className="absolute top-10 left-5 w-24 h-24 opacity-20 animate-float" />
                        <img src={bgElement2} alt="" className="absolute top-20 right-5 w-20 h-20 opacity-20 animate-float-delayed" />
                        <img src={bgElement3} alt="" className="absolute bottom-10 left-1/4 w-20 h-20 opacity-20 animate-float" />
                    </div>
                )}

                {/* White container for search elements */}
                <div className={`${props.showSearchBar ? 'relative z-10 bg-white rounded-3xl shadow-2xl p-6 w-full' : ''} flex flex-col gap-5`}>
                    {
                        props.showSearchBar &&
                        <h1 className="text-[32px] text-left leading-[38px] font-bold text-gray-800">Rendi Ükskõik<br />Mida</h1>
                    }
                    <div className="h-10 w-full" ref={searchBarRef}>
                        <div className={`${!searchBarColliding ? "relative" : "fixed top-[63px] left-[8px]"} z-[9] transition-all flex flex-row gap-2`}
                            style={searchBarColliding ? { width: `${document.documentElement.clientWidth - 16}px` } : undefined}
                        >
                            <div className="flex flex-col w-full gap-2">
                                <div className="flex flex-row gap-2">
                                    <div className="flex-grow">
                                        <SearchBar />
                                    </div>

                                    <div onClick={() => { setFilterOpened(!filterOpened) }} className="w-[50px] min-w-[50px] bg-primary flex justify-center items-center rounded-xl shadow-main cursor-pointer">
                                        <img src={filterOpened ? closeIcon : filterIcon} alt="" className={`${filterOpened ? "w-[20px]" : ""} w-[26px]`} />
                                    </div>
                                </div>

                                <div className={` ${filterOpened ? "h-fit" : "h-0"} w-full h-0 transition-all duration-300 bg-white rounded-xl shadow-main overflow-y-auto`} ref={fixedFilterContainerRef}>
                                    <FilterContainer containerMaxHeight={maxFiltersHeight} searchClick={() => { setFilterOpened(false) }} />
                                </div>
                            </div>

                        </div>


                    </div>

                    {
                        props.showSearchBar &&
                        <Categories />
                    }
                </div>
            </div>
            <div className="listview_container w-full flex flex-col lg:flex-row  gap-10 justify-start pt-2">

                <div className="flex flex-col gap-10 w-full pl-1 overflow-hidden">
                    <ListingListView />
                    {/* <ListingListViewCarousel /> */}
                </div>
            </div>
        </div>

    )
}